import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

const ExamResultViewer = () => {
    const { language } = useLanguage();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);

    useEffect(() => {
        const loadResult = async () => {
            try {
                // Hardcoded ID for demo purposes, in real app would come from params
                const data = await api.getExamResult('res-101');
                setResult(data);
            } catch (error) {
                console.error("Failed to load exam result", error);
            } finally {
                setLoading(false);
            }
        };
        loadResult();
    }, []);

    if (loading) {
        return <div className="p-8 text-center text-gray-500">{language === 'fr' ? 'Chargement du rapport...' : 'Loading report...'}</div>;
    }

    if (!result) return <div>No result found.</div>;

    const currentQuestion = result.questions[currentQuestionIndex];

    return (
        <div className="flex flex-col h-[calc(100vh-100px)] bg-gray-100 rounded-lg overflow-hidden shadow-lg border border-gray-200">
            {/* Header */}
            <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">
                        {language === 'fr' ? result.examTitle.fr : result.examTitle.en}
                    </h2>
                    <p className="text-sm text-gray-500">
                        Date: {result.date} • Score: <span className={`font-bold ${result.score >= 75 ? 'text-green-600' : 'text-red-600'}`}>{result.score}%</span>
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-sm text-gray-500">
                        Duration: {result.duration}
                    </div>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar Navigation */}
                <div className="w-20 md:w-64 bg-gray-50 border-r border-gray-200 flex flex-col overflow-y-auto custom-scrollbar">
                    <div className="p-4">
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 hidden md:block">
                            Questions
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            {result.questions.map((q, index) => (
                                <button
                                    key={q.id}
                                    onClick={() => {
                                        setCurrentQuestionIndex(index);
                                        setShowExplanation(false);
                                    }}
                                    className={`
                                        h-10 w-10 md:h-12 md:w-full rounded-md flex items-center justify-center md:justify-between md:px-3
                                        text-sm font-medium transition-colors
                                        ${currentQuestionIndex === index ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
                                        ${q.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                                    `}
                                >
                                    <span>{index + 1}</span>
                                    <span className="hidden md:block">
                                        {q.isCorrect ? '✓' : '✗'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-white custom-scrollbar">
                    <div className="max-w-3xl mx-auto">
                        <div className="mb-6 flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-500 uppercase">
                                Question {currentQuestionIndex + 1} / {result.totalQuestions}
                            </h3>
                            <div className={`px-3 py-1 rounded-full text-sm font-bold ${currentQuestion.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {currentQuestion.isCorrect ? (language === 'fr' ? 'Correct' : 'Correct') : (language === 'fr' ? 'Incorrect' : 'Incorrect')}
                            </div>
                        </div>

                        <div className="text-xl md:text-2xl font-serif text-gray-800 mb-10 leading-relaxed">
                            {currentQuestion.text}
                        </div>

                        <div className="space-y-4 mb-8">
                            <div className="p-4 rounded-lg border-2 border-transparent bg-gray-50">
                                <span className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                    {language === 'fr' ? 'Votre réponse' : 'Your Answer'}
                                </span>
                                <div className={`font-medium ${!currentQuestion.isCorrect ? 'text-red-700' : 'text-gray-900'}`}>
                                    {currentQuestion.userAnswer}
                                </div>
                            </div>

                            {!currentQuestion.isCorrect && (
                                <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
                                    <span className="block text-xs font-bold text-green-700 uppercase mb-1">
                                        {language === 'fr' ? 'Bonne réponse' : 'Correct Answer'}
                                    </span>
                                    <div className="font-medium text-green-900">
                                        {currentQuestion.correctAnswer}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Explanation Section */}
                        <div className="border-t border-gray-200 pt-6">
                            <button
                                onClick={() => setShowExplanation(!showExplanation)}
                                className="flex items-center text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
                            >
                                <svg
                                    className={`w-5 h-5 mr-2 transform transition-transform ${showExplanation ? 'rotate-180' : ''}`}
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                {language === 'fr' ? 'Afficher l\'explication' : 'Show Explanation'}
                            </button>

                            {showExplanation && (
                                <div className="mt-4 p-5 bg-blue-50 rounded-lg text-blue-900 leading-relaxed">
                                    <h4 className="font-bold mb-2 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {language === 'fr' ? 'Explication détaillée' : 'Detailed Explanation'}
                                    </h4>
                                    {currentQuestion.explanation}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Navigation */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
                <button
                    disabled={currentQuestionIndex === 0}
                    onClick={() => {
                        setCurrentQuestionIndex(prev => prev - 1);
                        setShowExplanation(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                    ← {language === 'fr' ? 'Précédent' : 'Previous'}
                </button>
                <button
                    disabled={currentQuestionIndex === result.totalQuestions - 1}
                    onClick={() => {
                        setCurrentQuestionIndex(prev => prev + 1);
                        setShowExplanation(false);
                    }}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                    {language === 'fr' ? 'Suivant' : 'Next'} →
                </button>
            </div>
        </div>
    );
};

export default ExamResultViewer;
